import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ResumoSessao,
  criarSessao,
  gravarSessaoAtiva,
  lerSessaoAtiva,
  listarResumos,
  listarSessoes,
  migrarSessaoLegada,
  removerSessao,
  renomearSessao,
  urlDaSessao,
} from "../../../core/session/sessionStore";

/*
 * Multi-conta — shell.
 *
 * Cada conta roda num <iframe> próprio, que carrega o CRM inteiro com o localStorage
 * isolado pelo `public/session-shim.js`. Os iframes ficam SEMPRE montados: quando uma
 * conta não está visível ela some por CSS (visibility/position), nunca por desmontagem
 * — é isso que mantém as duas vivas ao mesmo tempo, cada uma com seu realtime, seus
 * timers e seu próprio som de notificação.
 *
 * O shell não tem o shim, então lê o localStorage real e enxerga o namespace de cada
 * conta. É assim que ele descobre o e-mail de quem logou sem que o iframe precise
 * avisar nada.
 */

const INTERVALO_SYNC = 1500;

type Modo = "abas" | "dividido";

const MultiConta: React.FC = () => {
  const [resumos, setResumos] = useState<ResumoSessao[]>([]);
  const [ativa, setAtiva] = useState<string | null>(null);
  const [modo, setModo] = useState<Modo>("abas");
  const [renomeando, setRenomeando] = useState<string | null>(null);
  const [rascunhoRotulo, setRascunhoRotulo] = useState("");
  const montado = useRef(false);

  /*
   * O src de cada frame é decidido UMA vez e nunca mais muda. Se ele fosse derivado do
   * estado "logada", o frame recarregaria no instante em que a conta logasse — jogando
   * fora a sessão recém-criada. Depois do login quem navega para /chat é o próprio
   * React Router de dentro do frame, sem recarregar o documento (e o shim continua de pé).
   */
  const srcInicial = useRef<Map<string, string>>(new Map());
  const obterSrc = useCallback((r: ResumoSessao) => {
    const jaTem = srcInicial.current.get(r.sid);
    if (jaTem) return jaTem;
    const url = urlDaSessao(r.sid, r.logada ? "/chat" : "/entrar");
    srcInicial.current.set(r.sid, url);
    return url;
  }, []);

  const sincronizar = useCallback(() => {
    setResumos(listarResumos());
  }, []);

  // Bootstrap: adota a sessão que já estava aberta no modo antigo (para ligar o
  // multi-conta não deslogar ninguém) e garante pelo menos uma aba.
  useEffect(() => {
    if (montado.current) return;
    montado.current = true;

    migrarSessaoLegada();

    let sessoes = listarSessoes();
    if (sessoes.length === 0) {
      const nova = criarSessao();
      gravarSessaoAtiva(nova.sid);
      sessoes = [nova];
    }

    const salva = lerSessaoAtiva();
    const valida = salva && sessoes.some((s) => s.sid === salva) ? salva : sessoes[0].sid;
    setAtiva(valida);
    gravarSessaoAtiva(valida);
    sincronizar();
  }, [sincronizar]);

  // Mantém os rótulos em dia: quando uma conta conclui o login, o e-mail aparece na aba.
  useEffect(() => {
    const t = setInterval(sincronizar, INTERVALO_SYNC);
    return () => clearInterval(t);
  }, [sincronizar]);

  // Não lidas por conta, avisadas pelo /chat de dentro de cada frame.
  const [naoLidas, setNaoLidas] = useState<Record<string, number>>({});
  useEffect(() => {
    const aoReceber = (ev: MessageEvent) => {
      if (ev.origin !== window.location.origin) return;
      const d = ev.data;
      if (!d || d.tipo !== "crm:nao-lidas" || typeof d.sid !== "string") return;
      setNaoLidas((prev) =>
        prev[d.sid] === d.total ? prev : { ...prev, [d.sid]: Number(d.total) || 0 }
      );
    };
    window.addEventListener("message", aoReceber);
    return () => window.removeEventListener("message", aoReceber);
  }, []);

  const ativar = useCallback((sid: string) => {
    setAtiva(sid);
    gravarSessaoAtiva(sid);
  }, []);

  const adicionar = useCallback(() => {
    const nova = criarSessao();
    sincronizar();
    ativar(nova.sid);
    setModo("abas");
  }, [ativar, sincronizar]);

  const fechar = useCallback(
    (sid: string, rotulo: string) => {
      const total = listarSessoes().length;
      const msg =
        total <= 1
          ? `Encerrar a conta "${rotulo}"? Ela será deslogada e a lista ficará vazia.`
          : `Encerrar a conta "${rotulo}"? Ela será deslogada deste navegador.`;
      if (!window.confirm(msg)) return;

      removerSessao(sid);
      const restantes = listarSessoes();
      if (restantes.length === 0) {
        const nova = criarSessao();
        gravarSessaoAtiva(nova.sid);
        setAtiva(nova.sid);
      } else if (ativa === sid) {
        setAtiva(restantes[0].sid);
        gravarSessaoAtiva(restantes[0].sid);
      }
      sincronizar();
    },
    [ativa, sincronizar]
  );

  const confirmarRenome = useCallback(() => {
    if (renomeando) {
      renomearSessao(renomeando, rascunhoRotulo.trim());
      sincronizar();
    }
    setRenomeando(null);
    setRascunhoRotulo("");
  }, [renomeando, rascunhoRotulo, sincronizar]);

  // No modo dividido mostramos todas as contas lado a lado; no modo abas, só a ativa
  // fica visível (as outras seguem montadas e vivas).
  const visiveis = useMemo(
    () => (modo === "dividido" ? resumos.map((r) => r.sid) : ativa ? [ativa] : []),
    [modo, resumos, ativa]
  );

  const podeDividir = resumos.length > 1;

  return (
    <div className="mc-root">
      <style>{`
        .mc-root {
          position: fixed; inset: 0; display: flex; flex-direction: column;
          background: var(--mc-bg, #eef1f4); z-index: 1;
        }
        [data-theme="dark"] .mc-root { --mc-bg: #14181d; }

        .mc-barra {
          display: flex; align-items: center; gap: 6px;
          padding: 6px 10px; flex: 0 0 auto; overflow-x: auto;
          background: var(--mc-barra-bg, #fff);
          border-bottom: 1px solid var(--mc-borda, #dfe4ea);
        }
        [data-theme="dark"] .mc-barra { --mc-barra-bg: #1b2027; --mc-borda: #2b323b; }

        .mc-aba {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 7px 10px 7px 12px; border-radius: 8px 8px 0 0;
          border: 1px solid transparent; border-bottom: none;
          background: transparent; cursor: pointer; white-space: nowrap;
          font-size: 13px; color: var(--mc-txt, #45525f); max-width: 260px;
        }
        [data-theme="dark"] .mc-aba { --mc-txt: #aab4c0; }
        .mc-aba:hover { background: rgba(var(--brand-primary-rgb, 0,126,148), .08); }
        .mc-aba.ativa {
          background: var(--mc-bg, #eef1f4);
          border-color: var(--mc-borda, #dfe4ea);
          color: var(--brand-primary, #007e94); font-weight: 600;
        }
        .mc-aba .mc-nome { overflow: hidden; text-overflow: ellipsis; }

        .mc-ponto {
          width: 8px; height: 8px; border-radius: 50%; flex: 0 0 auto;
          background: #b6c0cb;
        }
        .mc-ponto.on { background: #22c55e; }

        .mc-badge {
          background: #e5484d; color: #fff; font-size: 11px; font-weight: 700;
          min-width: 18px; height: 18px; line-height: 18px; text-align: center;
          border-radius: 9px; padding: 0 5px; flex: 0 0 auto;
        }

        .mc-x {
          border: none; background: transparent; cursor: pointer; line-height: 1;
          font-size: 15px; color: #97a4b1; padding: 0 2px; border-radius: 4px;
        }
        .mc-x:hover { color: #e5484d; background: rgba(229,72,77,.12); }

        .mc-add, .mc-modo {
          border: 1px dashed var(--mc-borda, #dfe4ea); background: transparent;
          color: var(--brand-primary, #007e94); cursor: pointer;
          padding: 6px 12px; border-radius: 8px; font-size: 13px; white-space: nowrap;
        }
        .mc-modo { border-style: solid; }
        .mc-modo.on {
          background: var(--brand-primary, #007e94); color: #fff;
          border-color: var(--brand-primary, #007e94);
        }
        .mc-add:hover, .mc-modo:hover { filter: brightness(.95); }
        .mc-espaco { flex: 1 1 auto; }

        .mc-palco { flex: 1 1 auto; position: relative; display: flex; gap: 2px; }

        /* Painel escondido continua no DOM e com o iframe VIVO. Nada de display:none
           nem unmount: a conta precisa seguir recebendo mensagem e tocando som. */
        .mc-painel { position: relative; flex: 1 1 0; min-width: 0; }
        .mc-painel.oculto {
          position: absolute; inset: 0; flex: 0 0 auto; width: 100%;
          visibility: hidden; pointer-events: none; z-index: 0;
        }
        .mc-painel.visivel { visibility: visible; z-index: 1; }

        .mc-frame { width: 100%; height: 100%; border: none; display: block; background: #fff; }
        [data-theme="dark"] .mc-frame { background: #14181d; }

        .mc-cabeca {
          position: absolute; top: 6px; left: 50%; transform: translateX(-50%);
          background: rgba(0,0,0,.62); color: #fff; font-size: 11px;
          padding: 2px 10px; border-radius: 20px; z-index: 2; pointer-events: none;
        }

        .mc-renome {
          font-size: 13px; border: 1px solid var(--brand-primary, #007e94);
          border-radius: 6px; padding: 4px 6px; width: 150px;
        }
      `}</style>

      <div className="mc-barra">
        {resumos.map((r) => {
          const eAtiva = r.sid === ativa;
          return (
            <div
              key={r.sid}
              className={`mc-aba ${eAtiva ? "ativa" : ""}`}
              onClick={() => ativar(r.sid)}
              onDoubleClick={() => {
                setRenomeando(r.sid);
                setRascunhoRotulo(r.rotulo);
              }}
              title={
                r.logada
                  ? `${r.email ?? ""}${r.adminMaster ? " · admin master" : ""}`
                  : "Ainda não logada"
              }
            >
              <span className={`mc-ponto ${r.logada ? "on" : ""}`} />
              {renomeando === r.sid ? (
                <input
                  className="mc-renome"
                  autoFocus
                  value={rascunhoRotulo}
                  onChange={(e) => setRascunhoRotulo(e.target.value)}
                  onBlur={confirmarRenome}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") confirmarRenome();
                    if (e.key === "Escape") {
                      setRenomeando(null);
                      setRascunhoRotulo("");
                    }
                  }}
                  onClick={(e) => e.stopPropagation()}
                />
              ) : (
                <span className="mc-nome">{r.rotulo}</span>
              )}
              {!eAtiva && (naoLidas[r.sid] ?? 0) > 0 && (
                <span className="mc-badge" title={`${naoLidas[r.sid]} não lidas`}>
                  {naoLidas[r.sid] > 99 ? "99+" : naoLidas[r.sid]}
                </span>
              )}
              <button
                className="mc-x"
                title="Encerrar esta conta"
                onClick={(e) => {
                  e.stopPropagation();
                  fechar(r.sid, r.rotulo);
                }}
              >
                ×
              </button>
            </div>
          );
        })}

        <button className="mc-add" onClick={adicionar} title="Entrar com outra conta">
          + conta
        </button>

        <span className="mc-espaco" />

        {podeDividir && (
          <button
            className={`mc-modo ${modo === "dividido" ? "on" : ""}`}
            onClick={() => setModo(modo === "dividido" ? "abas" : "dividido")}
            title="Mostrar as contas lado a lado"
          >
            {modo === "dividido" ? "Ver uma" : "Ver lado a lado"}
          </button>
        )}
      </div>

      <div className="mc-palco">
        {resumos.map((r) => {
          const visivel = visiveis.includes(r.sid);
          return (
            <div
              key={r.sid}
              className={`mc-painel ${visivel ? "visivel" : "oculto"}`}
            >
              {modo === "dividido" && visivel && (
                <div className="mc-cabeca">{r.rotulo}</div>
              )}
              <iframe
                className="mc-frame"
                title={`Conta ${r.rotulo}`}
                name={`crmsess:${r.sid}`}
                src={obterSrc(r)}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MultiConta;
